// Streaming chat turn — provider-aware. Routes to "Bundle of Babies"
// (BabyAI MoE Space, the default) or Anthropic (BYOK). onToken fires per
// delta; the promise resolves with usage when the stream closes.
//
// Both paths are browser-direct (no BKA server). The document-editing
// behaviour is identical across providers because it rides the
// prompt-defined tag protocol, not native tool-calls.

import { getClient } from "./anthropic-client";
import { callBabyAIStream } from "./babyai-client";
import type { Provider } from "../store/keyStore";

export interface ChatTurnMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatTurnResult {
  content: string;
  promptTokens: number;
  completionTokens: number;
}

export interface StreamChatConfig {
  provider: Provider;
  token: string;
  model: string;
  history: ChatTurnMessage[];
  onToken: (delta: string) => void;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
}

export async function streamChat(cfg: StreamChatConfig): Promise<ChatTurnResult> {
  const maxTokens = cfg.maxTokens ?? 2048;
  const temperature = cfg.temperature ?? 0.7;
  const system = cfg.systemPrompt ?? "You are a helpful writing assistant.";

  if (cfg.provider === "babyai") {
    const r = await callBabyAIStream({
      token: cfg.token,
      model: cfg.model,
      messages: cfg.history.map((m) => ({ role: m.role, content: m.content })),
      systemInstruction: system,
      maxTokens,
      temperature,
      onToken: cfg.onToken,
    });
    return { content: r.content, promptTokens: r.promptTokens, completionTokens: r.completionTokens };
  }

  // anthropic
  const client = await getClient(cfg.token);
  const stream = client.messages.stream({
    model: cfg.model,
    max_tokens: maxTokens,
    system,
    temperature,
    messages: cfg.history.map((m) => ({ role: m.role, content: m.content })),
  });
  let fullText = "";
  stream.on("text", (delta: string) => { fullText += delta; cfg.onToken(delta); });
  const final = await stream.finalMessage();
  return {
    content: fullText,
    promptTokens: final.usage?.input_tokens ?? 0,
    completionTokens: final.usage?.output_tokens ?? 0,
  };
}
